/**
 * src/api/tenantApi.js
 *
 * Tenant management API — all operations go through here.
 * All storage calls are async (Azure-backed).
 *
 * NOTE: Super-admin authentication is no longer handled here.
 * superAdminLogin / seedSuperAdmin have been removed.
 *
 * Tenant user authentication (login) goes through the tenant-login Azure
 * Function, which validates credentials and returns a signed JWT.
 *
 * Environment routing (super-admin cross-environment management):
 *   Operations that the super-admin portal performs against another
 *   environment take a trailing `{ env }` option, forwarded to storage-azure.js
 *   which routes the request to that environment's Function App and names the
 *   tenant explicitly. Tenant callers omit it and work against this
 *   deployment with the tenant resolved from the JWT. That routing is the only
 *   delta — there is no separate env-parameterized copy of this module.
 */

import {
  readTenantRegistry,
  writeTenantRegistry,
  provisionTenant,
  readTenantUsers,
  writeTenantUsers,
  deleteTenantUser as remoteDeleteTenantUser,
  setUserPassword as remoteSetUserPassword,
  resetUserMfa as remoteResetUserMfa,
  readAccessRequests,
  writeAccessRequests,
  readFrameworkCatalogue,
  writeFrameworkCatalogue,
  deleteTenantData,
  readTenantStateStrict,
  readTenantStateStrictWithVersion,
  writeTenantStateVersioned,
  writeTenantStatePatch,
  writeDocument as remoteWriteDocument,
  callDocumentsWorkflow as remoteCallDocumentsWorkflow,
  deleteDocumentRow as remoteDeleteDocumentRow,
  addDocumentReaders as remoteAddDocumentReaders,
  removeDocumentReader as remoteRemoveDocumentReader,
  updateDocumentReader as remoteUpdateDocumentReader,
  bulkAssignDocumentReaders as remoteBulkAssignDocumentReaders,
  writeTenantState,
  readAppVersion as remoteReadAppVersion,
  writeAppVersion as remoteWriteAppVersion,
  readUiLayout as remoteReadUiLayout,
  writeUiLayout as remoteWriteUiLayout,
  readBulkMailRecipients as remoteReadBulkMailRecipients,
  writeBulkMailRecipients as remoteWriteBulkMailRecipients,
  sendBulkMail as remoteSendBulkMail,
} from './storage-azure.js';
import { azureEnvGet, azureEnvDelete } from './azureClient.js';
import { INITIAL_STATE } from '../state/initialState.js';
import { generateId, now } from '../utils/formatting.js';
import { getFunctionUrl } from './functionUrl.js';

// ── Tenant registry ───────────────────────────────────────────────────────────

/**
 * @param {{ env?: string }} [opts] - `env` reads another environment's registry
 *   (super-admin portal); omit for this deployment's own registry.
 */
export async function listTenants(opts = {}) {
  try {
    return await readTenantRegistry(opts);
  } catch (e) {
    console.error('[tenantApi] listTenants failed:', e.message);
    return [];
  }
}

export async function getTenantById(idOrSlug) {
  // The dev branch below needs to scan for the __dev__ placeholder under either
  // of its historical slugs, so it asks for the list; the production registry
  // refuses that and answers with an empty array, which is the correct result
  // there — no production registry contains a __dev__ tenant.
  const tenants =
    idOrSlug === '__dev__'
      ? await readTenantRegistry({ all: true })
      : await readTenantRegistry({ slug: idOrSlug });

  // Dev fallback — auto-create a demo tenant if it doesn't exist.
  // NOTE: With RLS hardening, anon cannot write to tenant_registry.
  // If auto-creation fails, create the dev tenant via the super-admin
  // portal or run:  node scripts/seed-dev-tenant.js
  if (idOrSlug === '__dev__') {
    let devTenant = tenants.find(
      (t) => t.id === '__dev__' || t.slug === '__dev__' || t.slug === '--dev--'
    );
    if (!devTenant) {
      try {
        devTenant = _createTenantRecord('Demo Company', 'demo');
        devTenant.id = '__dev__';
        devTenant.slug = '__dev__';
        await writeTenantRegistry([...tenants, devTenant]);
        await writeTenantState('__dev__', INITIAL_STATE);
      } catch (_e) {
        console.error(
          '[tenantApi] Dev tenant auto-creation blocked by RLS (expected).\n' +
            'Create the dev tenant via the super-admin portal or run the SQL seed manually.'
        );
        return null;
      }
    }
    return devTenant;
  }

  return tenants.find((t) => t.id === idOrSlug || t.slug === idOrSlug) || null;
}

function _createTenantRecord(name, slug) {
  return {
    id: generateId(),
    name,
    slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
    status: 'active',
    createdAt: now(),
    plan: 'standard',
    adminEmail: '',
  };
}

/**
 * createTenant — atomic server-side provisioning.
 *
 * The registry row, the initial state blob and the default administrator user
 * are created in one `tenant-provision` call, so a partial failure can never
 * leave a tenant that exists in the registry but has no state or no admin.
 *
 * @param {{ env?: string, nonProduction?: boolean }} [opts] - `env` provisions
 *   into another environment; `nonProduction` declares that the tenant holds no
 *   real customer data, which the dev/test registries require (GH #6444).
 */
export async function createTenant(name, slug, adminEmail, plan = 'standard', opts = {}) {
  const { tenant, temporaryPassword } = await provisionTenant(
    { name, slug, adminEmail, plan, nonProduction: opts.nonProduction === true },
    opts
  );
  return { ...tenant, temporaryPassword };
}

/**
 * aliasTenantSubdomain — calls the alias-tenant Azure Function.
 *
 * Exposed in the super-admin portal as a manual recovery action.
 *
 * Cross-environment aliasing has never been implemented: alias-tenant is only
 * reachable on this deployment's own Function App, so an `env` call resolves
 * to null rather than aliasing the wrong environment's subdomain.
 *
 * @param {{ env?: string }} [opts]
 */
export async function aliasTenantSubdomain(slug, opts = {}) {
  if (opts.env) return null;
  const res = await fetch(getFunctionUrl('alias-tenant'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `alias-tenant returned ${res.status}`);
  }
  return res.json(); // { alias, deployment }
}

/** @param {{ env?: string }} [opts] */
export async function updateTenant(id, changes, opts = {}) {
  const tenants = await readTenantRegistry(opts);
  const updated = tenants.map((t) => (t.id === id ? { ...t, ...changes } : t));
  await writeTenantRegistry(updated, opts);
  return updated.find((t) => t.id === id);
}

export async function getTenantByCustomDomain(hostname) {
  const tenants = await readTenantRegistry({ customDomain: hostname });
  const lower = hostname.toLowerCase();
  return tenants.find((t) => t.customDomain && t.customDomain.toLowerCase() === lower) || null;
}

/** @param {{ env?: string }} [opts] */
export async function suspendTenant(id, opts = {}) {
  return updateTenant(id, { status: 'suspended' }, opts);
}

/** @param {{ env?: string }} [opts] */
export async function reactivateTenant(id, opts = {}) {
  return updateTenant(id, { status: 'active' }, opts);
}

/** @param {{ env?: string }} [opts] */
export async function deleteTenant(id, opts = {}) {
  const tenants = await readTenantRegistry(opts);
  await writeTenantRegistry(
    tenants.filter((t) => t.id !== id),
    opts
  );
  await deleteTenantData(id, opts);
}

// ── Tenant user management ────────────────────────────────────────────────────

/** @param {{ env?: string }} [opts] */
export async function loadTenantUsers(tenantId, opts = {}) {
  return readTenantUsers(tenantId, opts);
}

/** @param {{ env?: string, replace?: boolean }} [opts] */
export async function saveTenantUsers(tenantId, users, opts = {}) {
  return writeTenantUsers(tenantId, users, opts);
}

/**
 * Delete one tenant user. Separate from saveTenantUsers because that path is
 * upsert-only — omitting a user from the saved array never deletes them.
 *
 * @param {{ env?: string, tenantId?: string }} [opts]
 */
export async function deleteTenantUser(userId, opts = {}) {
  return remoteDeleteTenantUser(userId, opts);
}

/** @param {{ env?: string, tenantId?: string }} [opts] */
export async function setUserPassword(userId, newPassword, opts = {}) {
  return remoteSetUserPassword(userId, newPassword, opts);
}

/**
 * Clear a user's MFA enrolment and issue a temporary password (recovery for a
 * user who lost their second factor).
 *
 * A tenant admin gets `{ ok, emailed, recipientEmail, expiresAt }` — the
 * password goes to the user by email and is never returned. A super-admin gets
 * `{ ok, tempPassword, expiresAt }` and no email is sent. See
 * storage-azure.resetUserMfa for why the two differ.
 *
 * @param {{ env?: string, tenantId?: string }} [opts]
 * @returns {Promise<{ ok: boolean, tempPassword?: string, emailed?: boolean, recipientEmail?: string, expiresAt: string }>}
 */
export async function resetUserMfa(userId, opts = {}) {
  return remoteResetUserMfa(userId, opts);
}

export async function markOnboardingComplete(tenantId, userId) {
  const users = await loadTenantUsers(tenantId);
  const updated = users.map((u) => (u.id === userId ? { ...u, onboarding_completed: true } : u));
  await saveTenantUsers(tenantId, updated);
}

// ── Tenant compliance state ───────────────────────────────────────────────────

export async function loadTenantState(tenantId) {
  // A lenient read returns null on error (network, 401, etc.) — distinguish
  // "genuinely empty tenant" from "load failed" by letting errors propagate.
  // Callers must not save state back if this throws.
  const state = await readTenantStateStrict(tenantId);
  return state || INITIAL_STATE;
}

export async function saveTenantState(tenantId, state) {
  return writeTenantState(tenantId, state);
}

// Document-scoped write — bypasses the full-state path. Used by saveDocument
// in useAppState so a Save click uploads one document instead of the whole
// tenant state. Server writes the doc as one row in tenant_documents.
export async function saveDocumentPatch(tenantId, payload) {
  // DOC-B-001: documents-write enforces status immutability server-side.
  // Catch it here too so callers get a clear dev-time error instead of a 403.
  if (payload && 'documentStatus' in payload) {
    throw new Error(
      '[saveDocumentPatch] documentStatus must not be passed here. ' +
        'Use callDocumentWorkflow() to advance document status.'
    );
  }
  return remoteWriteDocument(tenantId, payload);
}

// Document-scoped delete — removes the per-document row so the state-read
// merge can never resurrect a soft-deleted doc from the legacy state column.
// Complements the in-memory soft-delete in useAppState.deleteDocument.
export async function deleteDocumentRow(tenantId, docId) {
  return remoteDeleteDocumentRow(tenantId, docId);
}

// Document workflow transition — advances documentStatus through the
// server-side state machine. Must be used for ALL documentStatus changes
// (DOC-B-001). Returns { ok, doc } from the server.
export async function callDocumentWorkflow(tenantId, action, body) {
  return remoteCallDocumentsWorkflow(action, body);
}

// Document readers — the one list of who must read a document (DOC-B-026).
// Every add or remove goes through these, never updateState on
// state.documents (which usePersistence strips from blob saves, GH #6012).
export async function addDocumentReaders(documentId, body) {
  return remoteAddDocumentReaders(documentId, body);
}

export async function updateDocumentReader(documentId, change) {
  return remoteUpdateDocumentReader(documentId, change);
}

export async function removeDocumentReader(documentId, targetId, reason) {
  return remoteRemoveDocumentReader(documentId, targetId, reason);
}

export async function bulkAssignDocumentReaders(body) {
  return remoteBulkAssignDocumentReaders(body);
}

// ── Access requests ───────────────────────────────────────────────────────────

export const ACCESS_DURATION_OPTIONS = [
  { label: '1 hour', ms: 1 * 60 * 60 * 1000 },
  { label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { label: '1 day', ms: 24 * 60 * 60 * 1000 },
  { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
  { label: '1 month', ms: 30 * 24 * 60 * 60 * 1000 },
  { label: '6 months', ms: 182 * 24 * 60 * 60 * 1000 },
  { label: 'Indefinite', ms: null },
];

/** @param {{ env?: string }} [opts] */
export async function listAccessRequests(tenantId, opts = {}) {
  return readAccessRequests(tenantId, opts);
}

export async function createAccessRequest(
  tenantId,
  { requestedBy, requestedByName, reason, targetAdminId }
) {
  const requests = await readAccessRequests(tenantId);
  const req = {
    id: generateId(),
    tenantId,
    requestedBy,
    requestedByName,
    requestedAt: now(),
    reason,
    targetAdminId,
    status: 'pending',
    durationLabel: null,
    durationMs: null,
    expiresAt: null,
    token: generateId() + generateId(),
    resolvedAt: null,
    resolvedBy: null,
    auditLog: [{ at: now(), action: 'requested', by: requestedByName, note: reason }],
  };
  await writeAccessRequests(tenantId, [req, ...requests]);
  return req;
}

export async function approveAccessRequest(
  tenantId,
  requestId,
  { approvedBy, approvedByName, durationLabel, durationMs }
) {
  const requests = await readAccessRequests(tenantId);
  const expiresAt = durationMs ? new Date(Date.now() + durationMs).toISOString() : null;
  const updated = requests.map((r) =>
    r.id === requestId
      ? {
          ...r,
          status: 'approved',
          durationLabel,
          durationMs,
          expiresAt,
          resolvedAt: now(),
          resolvedBy: approvedBy,
          auditLog: [
            ...(r.auditLog || []),
            {
              at: now(),
              action: 'approved',
              by: approvedByName,
              note: `Access granted for ${durationLabel}`,
            },
          ],
        }
      : r
  );
  await writeAccessRequests(tenantId, updated);
  return updated.find((r) => r.id === requestId);
}

export async function denyAccessRequest(tenantId, requestId, { deniedBy, deniedByName, note }) {
  const requests = await readAccessRequests(tenantId);
  const updated = requests.map((r) =>
    r.id === requestId
      ? {
          ...r,
          status: 'denied',
          resolvedAt: now(),
          resolvedBy: deniedBy,
          auditLog: [
            ...(r.auditLog || []),
            { at: now(), action: 'denied', by: deniedByName, note: note || 'Request denied' },
          ],
        }
      : r
  );
  await writeAccessRequests(tenantId, updated);
  return updated.find((r) => r.id === requestId);
}

export async function revokeAccessRequest(
  tenantId,
  requestId,
  { revokedBy: _revokedBy, revokedByName }
) {
  const requests = await readAccessRequests(tenantId);
  const updated = requests.map((r) =>
    r.id === requestId
      ? {
          ...r,
          status: 'revoked',
          expiresAt: now(),
          auditLog: [
            ...(r.auditLog || []),
            {
              at: now(),
              action: 'revoked',
              by: revokedByName,
              note: 'Access revoked by tenant admin',
            },
          ],
        }
      : r
  );
  await writeAccessRequests(tenantId, updated);
  return updated.find((r) => r.id === requestId);
}

export async function validateAccessToken(tenantId, token) {
  const requests = await readAccessRequests(tenantId);
  const req = requests.find((r) => r.token === token && r.status === 'approved');
  if (!req) return null;
  if (req.expiresAt && new Date(req.expiresAt) < new Date()) {
    const updated = requests.map((r) => (r.id === req.id ? { ...r, status: 'expired' } : r));
    await writeAccessRequests(tenantId, updated);
    return null;
  }
  return req;
}

export async function getActiveAccessRequest(tenantId) {
  const requests = await readAccessRequests(tenantId);
  return (
    requests.find((r) => {
      if (r.status !== 'approved') return false;
      if (r.expiresAt && new Date(r.expiresAt) < new Date()) return false;
      return true;
    }) || null
  );
}

// ── Framework catalogue ────────────────────────────────────────────────────────

/** @param {{ env?: string }} [opts] */
export async function loadFrameworkCatalogue(opts = {}) {
  return await readFrameworkCatalogue(opts);
}

/** @param {{ env?: string }} [opts] */
export async function saveFrameworkCatalogue(frameworks, opts = {}) {
  return await writeFrameworkCatalogue(frameworks, opts);
}

// ── Admin-strict tenant state — errors propagate (for super admin push) ──────

/**
 * Read a tenant's state together with its hex ROWVERSION. Pass the version
 * back to saveTenantStateStrict so a concurrent write is rejected with a 409
 * instead of silently clobbering the other writer.
 *
 * @param {{ env?: string }} [opts]
 * @returns {Promise<{ state: object | null, stateVersion: string | null }>}
 *   `state: null` = no row; a throw = a failed read
 */
export async function loadTenantStateStrict(tenantId, opts = {}) {
  return await readTenantStateStrictWithVersion(tenantId, undefined, opts);
}

/** @param {{ env?: string }} [opts] */
export async function saveTenantStateStrict(tenantId, state, stateVersion, opts = {}) {
  await writeTenantStateVersioned(tenantId, state, stateVersion, opts); // throws on failure
}

export async function loadTenantStateWithVersion(tenantId, signal) {
  const { state, stateVersion } = await readTenantStateStrictWithVersion(tenantId, signal);
  return { state: state || INITIAL_STATE, stateVersion };
}

export async function saveTenantStateVersioned(tenantId, state, stateVersion, options) {
  // Returns the new hex ROWVERSION so usePersistence can refresh its cached
  // version after a successful write and avoid a self-inflicted 409.
  return writeTenantStateVersioned(tenantId, state, stateVersion, options);
}

/** CONC-01: save only what changed, applied by the server to the current row. */
export async function saveTenantStatePatch(tenantId, ops, options) {
  return writeTenantStatePatch(tenantId, ops, options);
}

// ── App version / UI layout (per-environment registry values) ───────────────

/** @param {{ env?: string }} [opts] */
export async function readAppVersion(opts = {}) {
  return remoteReadAppVersion(opts);
}

/** @param {{ env?: string }} [opts] */
export async function writeAppVersion(version, opts = {}) {
  return remoteWriteAppVersion(version, opts);
}

/** @param {{ env?: string }} [opts] */
export async function readUiLayout(opts = {}) {
  return remoteReadUiLayout(opts);
}

export async function readBulkMailRecipients() {
  return remoteReadBulkMailRecipients();
}

export async function writeBulkMailRecipients(recipients) {
  return remoteWriteBulkMailRecipients(recipients);
}

export async function sendBulkMail(payload) {
  return remoteSendBulkMail(payload);
}

/** @param {{ env?: string }} [opts] */
export async function writeUiLayout(layoutId, opts = {}) {
  return remoteWriteUiLayout(layoutId, opts);
}

// ── Diagnostics (issue #732) ────────────────────────────────────────────────
//
// Super-admin only: the diagnostics endpoint is reached cross-environment from
// the admin portal, so these take the same `{ env }` option as the operations
// above. There is no tenant-side caller.

const DIAGNOSTICS_TTL_HOURS = 24;

/**
 * Enable diagnostics for a tenant for 24h. The flag is stored inside the
 * tenant record in the registry — we use the same updateTenant write path.
 *
 * @param {{ env?: string }} [opts]
 */
export async function enableDiagnostics(tenantId, enabledBy, opts = {}) {
  const expiresAt = new Date(Date.now() + DIAGNOSTICS_TTL_HOURS * 3600_000).toISOString();
  const diagnostics = { enabled: true, expiresAt, enabledBy: enabledBy || '' };
  await updateTenant(tenantId, { diagnostics }, opts);
  return diagnostics;
}

/**
 * Disable diagnostics for a tenant immediately. Pending events already in
 * the database are NOT purged — use purgeDiagnosticsEvents() for that.
 *
 * @param {{ env?: string }} [opts]
 */
export async function disableDiagnostics(tenantId, opts = {}) {
  await updateTenant(tenantId, { diagnostics: null }, opts);
}

/**
 * Read recent diagnostic events for a tenant. Super-admin only on the server.
 *
 * @param {{ env?: string }} [opts]
 */
export async function readDiagnosticsEvents(tenantId, filters = {}, opts = {}) {
  const params = { tenantId };
  if (filters.userId) params.userId = filters.userId;
  if (filters.eventType) params.eventType = filters.eventType;
  if (filters.since) params.since = filters.since;
  if (filters.until) params.until = filters.until;
  if (filters.limit) params.limit = filters.limit;
  const data = await azureEnvGet(opts.env, '/api/diagnostics', params);
  return Array.isArray(data.events) ? data.events : [];
}

/**
 * Purge all diagnostic events for a tenant. Does not change the enable flag.
 *
 * @param {{ env?: string }} [opts]
 */
export async function purgeDiagnosticsEvents(tenantId, opts = {}) {
  const data = await azureEnvDelete(opts.env, '/api/diagnostics', { tenantId });
  return data?.deleted ?? 0;
}
